import sanitizeHtml from 'sanitize-html';

const COLOR = /^(?:#[0-9a-f]{3,8}|rgba?\([\d.,%\s]+\)|[a-z]+)$/i;
const LENGTHS = /^\d+(?:\.\d+)?(?:px|pt|em|rem|%)?(?:\s+\d+(?:\.\d+)?(?:px|pt|em|rem|%)?){0,3}$/;
const INLINE_IMAGE = /^data:image\/(?:png|jpeg|gif);base64,[a-z0-9+/=\r\n]+$/i;

/** Preserve the email template's typography without allowing scripts or page-level CSS. */
export function sanitizeEmailDraftHtml(html: string): string {
  return sanitizeHtml(html, {
    allowedTags: [...sanitizeHtml.defaults.allowedTags, 'font', 'img'],
    allowedAttributes: {
      '*': ['style'],
      a: ['href', 'target', 'rel'],
      table: ['width', 'border', 'cellspacing', 'cellpadding'],
      th: ['colspan', 'rowspan', 'align', 'valign', 'bgcolor'],
      td: ['colspan', 'rowspan', 'align', 'valign', 'bgcolor'],
      font: ['face', 'size', 'color'],
      img: ['src', 'alt', 'width', 'height'],
    },
    transformTags: {
      a: sanitizeHtml.simpleTransform('a', { target: '_blank', rel: 'noopener noreferrer' }),
      img: (tagName, attribs) => {
        const src = attribs.src ?? '';
        return {
          tagName,
          attribs: { ...attribs, src: /^data:/i.test(src) && !INLINE_IMAGE.test(src) ? '' : src },
        };
      },
    },
    allowedStyles: {
      '*': {
        'font-family': [/^[\w\s"',-]+$/],
        'font-size': [LENGTHS],
        'font-weight': [/^(?:normal|bold|[1-9]00)$/],
        'font-style': [/^(?:normal|italic)$/],
        'line-height': [LENGTHS, /^normal$/],
        color: [COLOR],
        'background-color': [COLOR],
        'text-align': [/^(?:left|right|center)$/],
        'vertical-align': [/^(?:top|middle|bottom)$/],
        'text-decoration': [/^(?:none|underline|line-through)$/],
        margin: [LENGTHS],
        // Outlook signatures use longhands to suppress default paragraph spacing.
        'margin-top': [LENGTHS],
        'margin-right': [LENGTHS],
        'margin-bottom': [LENGTHS],
        'margin-left': [LENGTHS],
        padding: [LENGTHS],
        'padding-top': [LENGTHS],
        'padding-right': [LENGTHS],
        'padding-bottom': [LENGTHS],
        'padding-left': [LENGTHS],
        width: [LENGTHS],
        height: [LENGTHS],
        'max-width': [LENGTHS],
        border: [/^(?:0|none)$/i, /^\d+(?:px|pt) solid (?:#[0-9a-f]{3,8}|rgba?\([\d.,%\s]+\)|transparent)$/i],
        'border-style': [/^(?:none|solid)$/],
        'border-width': [LENGTHS],
        'border-collapse': [/^collapse$/],
        'border-spacing': [LENGTHS],
      },
    },
    allowedSchemes: ['https', 'http', 'mailto'],
    allowedSchemesByTag: { img: ['https', 'http', 'data'] },
  });
}
